import cx from 'classnames';
import './SectionHeading.css';

/**
 * QHS SectionHeading
 * ------------------
 * Heading + optional description + optional trailing slot for the
 * top of a form section (Account Edit / Page settings / etc.). Draws
 * a bottom border so the body reads as a grouped block without a
 * surrounding card. Use `FieldGroup` when you need a card surface.
 *
 *   <SectionHeading
 *     title="Identity"
 *     description="Who runs this account"
 *     actions={<Button>…</Button>}
 *   />
 */
const SectionHeading = ({ title, description, actions, className, children, ...rest }) => (
  <header className={cx('qhs-section-heading', className)} {...rest}>
    <div className='qhs-section-heading__titles'>
      {title && <h2 className='qhs-section-heading__title'>{title}</h2>}
      {description && <p className='qhs-section-heading__desc'>{description}</p>}
      {children}
    </div>
    {actions && <div className='qhs-section-heading__actions'>{actions}</div>}
  </header>
);

/**
 * SubHeading — the canonical section divider for forms in the QHS
 * design system: short label on the left, thin rule line stretches
 * to the right, optional trailing slot, optional description on the
 * second row. Used at the top of every form section in
 * profile-edit, account-edit, and order edit/create.
 *
 *   <SubHeading>Basic info</SubHeading>
 *   <SubHeading description="Read-only — managed in the patient profile">
 *     Patient information
 *   </SubHeading>
 *   <SubHeading trailing={<Toggle … />}>Notifications</SubHeading>
 *
 * Always prefer SubHeading over SectionHeading for sections inside
 * a form — the rule-on-the-right layout is the project-wide rhythm.
 * SectionHeading (h2 + border-bottom) is reserved for page-level
 * settings dividers where a heavier separator is wanted.
 */
const SubHeading = ({ children, description, trailing, className, ...rest }) => (
  <div className={cx('qhs-sub-heading-block', className)} {...rest}>
    <div className='qhs-sub-heading'>
      <span className='qhs-sub-heading__label'>{children}</span>
      <span className='qhs-sub-heading__rule' aria-hidden='true' />
      {trailing && <span className='qhs-sub-heading__trailing'>{trailing}</span>}
    </div>
    {description && <p className='qhs-sub-heading__desc'>{description}</p>}
  </div>
);

SectionHeading.Sub = SubHeading;

export { SectionHeading, SubHeading };
export default SectionHeading;
